import React, { useState } from 'react';
import PageHero from '../components/PageHero';
import CtaSection from '../components/CtaSection';
import SourceConnectionCard from '../components/SourceConnectionCard';
import { ArchImage, ArrowLink, Label, Rail, Reveal, RuledSpread, Section, SectionHead } from '../components/System';
import useSeo from '../lib/useSeo';

// Only list what the product actually connects to today (see v1/backend/app/connectors/factory.py).
// Snowflake is on the roadmap (Innovation page) — add it here once the connector ships.
const CONNECTORS = ['POSTGRESQL', 'MYSQL', 'CSV & XLSX'];

const CONNECTOR_DETAILS = {
  POSTGRESQL: { type: 'Live connection', auth: 'Username and password. Read-only. Encrypted.', fit: 'Postgres databases of any size.' },
  MYSQL: { type: 'Live connection', auth: 'Username and password. Encrypted.', fit: 'App databases and older MySQL or MariaDB.' },
  'CSV & XLSX': { type: 'File upload', auth: 'None. Uploads stay private to your firm.', fit: 'Spreadsheets you want to use today.' },
};

const SEMANTIC_ROLES = [
  { title: 'Category', detail: 'Group by — department, region' },
  { title: 'Amount', detail: 'Numbers you add up — budget, revenue' },
  { title: 'Date', detail: 'When it happened' },
  { title: 'Reference', detail: 'ID numbers' },
  { title: 'Target', detail: 'What the question is about' },
  { title: 'Skip', detail: 'Columns to ignore' },
];

function CompanyDataCloud() {
  const [selected, setSelected] = useState(CONNECTORS[0]);
  const detail = CONNECTOR_DETAILS[selected];

  useSeo({
    title: 'Company Data Cloud: Connect SQL or Spreadsheets',
    path: '/company-data-cloud',
    description:
      'Connect PostgreSQL or MySQL, or drop in a spreadsheet. Sidwell reads your tables, works out what each column means, and leaves your data where it is.',
  });

  return (
    <main className="relative w-full">
      <PageHero
        image="facade"
        eyebrow="Company Data Cloud"
        title="Every source. One place."
        subtitle="Connect a database or drop in a spreadsheet. Your data stays where it is."
        badge="Three ways to connect"
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
      />

      <Section tone="white" className="lg:pt-[calc(clamp(56px,6vw,88px)+64px)]">
        <div className="shell">
          <SectionHead label="Your data cloud" title="Pick a source." aside="See what connecting each one involves." />
          <div className="grid-12 mt-12 gap-y-8 lg:mt-16">
            {/* The source picker sits on a plate; its details read as a statement beside it. */}
            <div className="relative col-span-4 lg:col-span-7">
              <ArchImage name="colonnade" sizes="(min-width: 1024px) 56vw, 100vw" className="absolute inset-0 h-full w-full" />
              <div className="relative p-4 sm:p-10">
                <SourceConnectionCard sources={CONNECTORS} selected={selected} onSelect={setSelected} />
              </div>
            </div>
            <div className="col-span-4 self-center lg:col-span-5">
              <div key={selected} className="swap-in">
                <p className="font-display text-[clamp(28px,2.8vw,42px)] font-light leading-tight">{selected}</p>
                <dl className="m-0 mt-6 border-b border-wall/25">
                  {[['Type', detail.type], ['What you enter', detail.auth], ['Best fit', detail.fit]].map(([k, v]) => (
                    <div key={k} className="border-t border-wall/25 py-5">
                      <dt className="label mb-2 text-bronze-deep">{k}</dt>
                      <dd className="m-0 text-[clamp(18px,1.5vw,21px)] leading-snug">{v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section tone="stone">
        <div className="shell">
          <Rail label="No setup project" title="Sidwell reads your columns." aside="Add a table. Sidwell labels each column. Fix any mistake in one click.">
            <RuledSpread items={SEMANTIC_ROLES} cols={2} />
          </Rail>
        </div>
      </Section>

      <Section tone="wall" pad="sm">
        <div className="shell">
          <Reveal className="grid-12 items-end gap-y-6">
            <div className="col-span-4 lg:col-span-8">
              <Label dark className="mb-5">Encrypted</Label>
              <h2 className="type-h2">Your password stays hidden.</h2>
              <p className="mt-4 text-glass-light">Encrypted on entry. Never shown again. Not even to us.</p>
            </div>
            <div className="col-span-4 lg:col-span-4 lg:text-right">
              <ArrowLink to="/security" className="text-limestone">Security &amp; privacy</ArrowLink>
            </div>
          </Reveal>
        </div>
      </Section>

      <CtaSection
        image="entrance"
        eyebrow="Get Started"
        title="Connect your first source."
        subtitle="Ready in minutes."
        primary={{ label: 'Request a demo', to: '/#request-briefing' }}
        secondary={{ label: 'Back to Overview', to: '/' }}
      />
    </main>
  );
}

export default CompanyDataCloud;
